import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { BillingInvoiceDto, CancelSubscriptionResponse, SubscriptionDto } from "@/types/api";

/**
 * Real billing history — the account's actual Stripe invoices (newest first),
 * with hosted-invoice and PDF links. Empty until the first paid checkout.
 */
export function useBillingInvoices() {
  return useQuery({
    queryKey: ["billing", "invoices", "me"],
    queryFn: ({ signal }) =>
      api.get<BillingInvoiceDto[]>("/api/billing/invoices", signal),
    staleTime: 60_000
  });
}

/**
 * Full billing history for the current account — newest first.
 * Distinct from <code>useAccount()</code>'s nested `active_subscription`
 * (which is summary-only).
 */
export function useSubscriptions() {
  return useQuery({
    queryKey: ["subscriptions", "me"],
    queryFn: ({ signal }) =>
      api.get<SubscriptionDto[]>("/api/accounts/me/subscriptions", signal),
    staleTime: 60_000
  });
}

/**
 * User-initiated cancellation of one of their own subscriptions.
 * Invalidates both the account summary (so the "current plan" hero
 * clears) and the history table.
 */
export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (subscriptionId: number) =>
      api.post<CancelSubscriptionResponse>(
        `/api/accounts/me/subscriptions/${subscriptionId}/cancel`
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["accounts", "me"] });
      qc.invalidateQueries({ queryKey: ["subscriptions", "me"] });
    }
  });
}
